<template>
  <div class="user-annotation-terms-panel">
    <div v-if="aiOntologies.length" class="ai-ontology-summary">
      <div
        v-for="ontology in aiOntologies"
        :key="ontology.id"
        class="ai-ontology-group"
        role="group"
        :aria-label="ontology.name"
      >
        <div class="ai-ontology-heading">
          <span class="ai-badge">AI</span>
          <span class="ai-ontology-name">{{ ontology.name }}</span>
        </div>
        <div v-if="aiOntologyTerms(ontology).length" class="ai-term-tags">
          <span
            v-for="term in aiOntologyTerms(ontology)"
            :key="term.id"
            class="ai-term-tag"
          >
            <span class="ai-term-dot" :style="{ background: term.color || '#6CB6D9' }"></span>
            <span>{{ term.name }}</span>
          </span>
        </div>
      </div>
    </div>

    <div v-if="userOntologies.length" class="ontology-tags field is-grouped is-grouped-multiline mb-3">
      <div class="control" v-for="ontology in userOntologies" :key="ontology.id">
        <div class="tags has-addons">
          <span class="tag is-info">{{ ontology.name }}</span>
          <a
            v-if="canManageProject"
            class="tag is-delete"
            :title="$t('remove-terms-from-image')"
            @click="removeOntologyFromImage(ontology.id)"
          ></a>
        </div>
      </div>
    </div>

    <div class="term-list">
      <annotation-term-row
        v-for="term in termRows"
        :key="term.id"
        :term="term"
        :state="termState(term)"
        :visible="getTermVisibility(term)"
        :show-visibility="Boolean(term.id)"
        :terms="terms"
        :images="images"
        paginated
        :current-page="termState(term).page + 1"
        :total="termState(term).total"
        :per-page="pageSize"
        @toggle="toggleTerm(term)"
        @toggle-visibility="toggleTermVisibility(term)"
        @retry="loadAnnotations(term)"
        @page-change="loadAnnotations(term, $event - 1)"
        @updateTermsOrTracks="$emit('updateTermsOrTracks', $event)"
        @centerView="$emit('centerView', $event)"
        @delete="$emit('delete', $event)"
        @select="$emit('select', $event)"
      />
    </div>

    <div class="has-text-right mt-2">
      <button class="button is-small is-link" @click="$emit('open-add-terms')">{{ $t('add-terms') }}</button>
    </div>
  </div>
</template>

<script>
import { Annotation } from '@/api';
import Cytomine from '@/api/cytomine';
import AnnotationTermRow from './AnnotationTermRow';
import viewerImageContext from './mixins/viewer-image-context';
import { getAllTerms } from '@/utils/ontology-utils';

const NO_TERM_ID = 0;

export default {
  name: 'user-annotation-terms-panel',
  mixins: [viewerImageContext],
  components: {
    AnnotationTermRow
  },
  props: {
    index: String,
    project: {
      type: Object,
      default: null
    },
    algorithmOntologyIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      pageSize: 24,
      annotationContextVersion: 0,
      states: {}
    };
  },
  computed: {
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    images() {
      return this.image ? [this.image] : [];
    },
    imageId() {
      return this.image?.id;
    },
    ontologyTerms() {
      return this.imageWrapper.style.ontologyTerms;
    },
    userOntologies() {
      return (this.imageWrapper.ontologies || [])
        .filter(ontology => !this.isAlgorithmOntology(ontology));
    },
    aiOntologies() {
      return (this.imageWrapper.ontologies || [])
        .filter(ontology => this.isAlgorithmOntology(ontology));
    },
    terms() {
      return this.userOntologies.flatMap(ontology => {
        if (!ontology || !ontology.children || !Array.isArray(ontology.children)) {
          return [];
        }

        return getAllTerms(ontology).map(term => ({
          ...term,
          ontologyId: ontology.id
        }));
      });
    },
    termRows() {
      const terms = this.terms
        .filter(term => !this.hiddenTermsIds.includes(term.id))
        .map(term => ({
          ...term,
          color: term.color || '#9575CD',
          colorHex: (term.color || '#9575CD').replace(/^#/, ''),
          noTerm: false
        }));

      return [
        ...terms,
        {
          id: NO_TERM_ID,
          name: this.$t('no-term'),
          color: '#9CA3AF',
          colorHex: '9CA3AF',
          noTerm: true
        }
      ];
    },
    hiddenTermsIds() {
      return this.$store.getters[this.imageModule + 'hiddenTermsIds'] || [];
    }
  },
  watch: {
    imageId(newImageId, oldImageId) {
      if (newImageId && newImageId !== oldImageId) {
        this.annotationContextVersion++;
        this.states = {};
      }
    }
  },
  methods: {
    isAlgorithmOntology(ontology) {
      return this.algorithmOntologyIds.includes(ontology.id) || /\s\(AI\)$/.test(ontology.name || '');
    },
    aiOntologyTerms(ontology) {
      if (!ontology || !Array.isArray(ontology.children)) {
        return [];
      }
      return getAllTerms(ontology);
    },
    stateKey(term) {
      return term.noTerm ? 'no-term' : String(term.id);
    },
    termState(term) {
      const key = this.stateKey(term);
      if (!this.states[key]) {
        this.$set(this.states, key, {
          expanded: false,
          loading: false,
          error: false,
          items: [],
          page: 0,
          total: 0,
          hasMore: false,
          requestId: 0,
          term
        });
      }
      return this.states[key];
    },
    async toggleTerm(term) {
      const state = this.termState(term);
      state.expanded = !state.expanded;

      if (state.expanded && !state.items.length && !state.loading) {
        await this.loadAnnotations(term);
      }
    },
    getTermVisibility(term) {
      if (this.ontologyTerms && this.ontologyTerms[term.id]) {
        return this.ontologyTerms[term.id].visible;
      }
      return true;
    },
    toggleTermVisibility(term) {
      this.$store.commit(this.imageModule + 'toggleOntologyTermVisibility', { termId: term.id });
    },
    async removeOntologyFromImage(ontologyId) {
      if (!confirm('Are you sure you want to remove these terms from the image?')) {
        return;
      }

      try {
        await this.$store.dispatch(this.imageModule + 'removeOntologyFromImage', ontologyId);
        this.$notify({ type: 'success', text: this.$t('notif-success-remove-ontology-from-image') });
      } catch (error) {
        console.error('Error removing ontology from image:', error);
        this.$notify({ type: 'error', text: this.$t('notif-error-remove-ontology-from-image') });
      }
    },
    normalizeAnnotation(raw) {
      const annot = raw instanceof Annotation ? raw : new Annotation(raw);
      const basePath = `${Cytomine.instance.host}${Cytomine.instance.basePath}`;
      annot.cropURL = annot.cropURL || `${basePath}userannotation/${annot.id}/crop.png`;
      annot.smallCropURL = annot.smallCropURL || `${basePath}userannotation/${annot.id}/crop.png?maxSize=256`;
      return annot;
    },
    async loadAnnotations(term, page = 0) {
      const state = this.termState(term);
      const nextPage = page;
      const contextVersion = this.annotationContextVersion;
      const requestId = state.requestId + 1;
      const imageId = this.imageId;
      state.requestId = requestId;

      state.loading = true;
      state.error = false;

      try {
        if (!imageId || !this.project || !this.project.id) {
          throw new Error('Cannot load user annotations without image and project context.');
        }

        const params = {
          images: String(imageId),
          max: this.pageSize,
          multipleTerm: false,
          noTerm: term.noTerm,
          offset: nextPage * this.pageSize,
          project: this.project.id,
          reviewed: false,
          showAlgo: false,
          showDefault: true,
          showGIS: true,
          showImage: false,
          showImageGroup: true,
          showTerm: true,
          showWKT: true
        };

        if (!term.noTerm) {
          params.term = term.id;
        }

        const { data } = await Cytomine.instance.api.post('annotation/search.json', params);
        if (contextVersion !== this.annotationContextVersion || requestId !== state.requestId || imageId !== this.imageId) {
          return;
        }

        const items = (data.collection || []).map(raw => this.normalizeAnnotation(raw));
        state.items = items;
        state.page = nextPage;
        const total = Number(data.size);
        state.total = Number.isFinite(total) ? total : items.length;
        state.hasMore = false;
      } catch (error) {
        if (contextVersion !== this.annotationContextVersion || requestId !== state.requestId || imageId !== this.imageId) {
          return;
        }
        console.error('Failed to load user annotations:', error);
        state.error = true;
        state.hasMore = false;
      } finally {
        if (contextVersion === this.annotationContextVersion && requestId === state.requestId && imageId === this.imageId) {
          state.loading = false;
        }
      }
    },
    refreshAnnotations(payload) {
      const idImage = payload && typeof payload === 'object' ? (payload.idImage || payload.image) : payload;
      if (!this.image || (idImage !== undefined && idImage !== null && idImage !== this.image.id)) {
        return;
      }

      Object.values(this.states).forEach(state => {
        if (state.expanded) {
          state.items = [];
          state.page = 0;
          state.total = 0;
          state.hasMore = false;
          this.loadAnnotations(state.term);
        }
      });
    }
  },
  mounted() {
    this.$eventBus.$on('addAnnotation', this.refreshAnnotations);
    this.$eventBus.$on('reloadAnnotations', this.refreshAnnotations);
    this.$eventBus.$on('editAnnotation', this.refreshAnnotations);
    this.$eventBus.$on('deleteAnnotation', this.refreshAnnotations);
  },
  beforeDestroy() {
    this.$eventBus.$off('addAnnotation', this.refreshAnnotations);
    this.$eventBus.$off('reloadAnnotations', this.refreshAnnotations);
    this.$eventBus.$off('editAnnotation', this.refreshAnnotations);
    this.$eventBus.$off('deleteAnnotation', this.refreshAnnotations);
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

.user-annotation-terms-panel {
  color: $dark-text-primary;
}

.ai-ontology-summary {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-bottom: 0.85rem;
}

.ai-ontology-group {
  padding: 0.65rem 0.7rem;
  border: 1px solid rgba(108, 182, 217, 0.2);
  border-radius: 8px;
  background: rgba(108, 182, 217, 0.055);
}

.ai-ontology-heading {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.ai-badge {
  flex: 0 0 auto;
  padding: 0.1rem 0.38rem;
  border: 1px solid rgba($primary, 0.4);
  border-radius: 4px;
  background: rgba($primary, 0.14);
  color: $primary;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.35;
}

.ai-ontology-name {
  overflow: hidden;
  color: $dark-text-primary;
  font-size: 0.82rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-term-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.55rem;
}

.ai-term-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  max-width: 100%;
  padding: 0.2rem 0.45rem;
  border: 1px solid rgba(190, 214, 238, 0.13);
  border-radius: 999px;
  background: rgba(222, 238, 255, 0.055);
  color: $dark-text-secondary;
  font-size: 0.72rem;
  line-height: 1.35;
}

.ai-term-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
}

.term-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

</style>
